
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCTA from "@/components/ProductCTA";
import { articles } from "@/data/articles";
import ArticleHeader from "@/components/article/ArticleHeader";
import ArticleContent from "@/components/article/ArticleContent";
import RecommendedProducts from "@/components/article/RecommendedProducts";
import SourcesList from "@/components/article/SourcesList";
import ShareButtons from "@/components/article/ShareButtons";
import ArticleSidebar from "@/components/article/ArticleSidebar";
import NotFoundArticle from "@/components/article/NotFoundArticle";

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;
  
  if (!article) {
    return <NotFoundArticle />;
  }

  // Check if this is the Hey Humans article to customize the CTA
  const isHeyHumansArticle = slug === "hey-humans-deodorant-review-aluminum-free";

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{article.title} | Armpit Care Guide</title>
        <meta name="description" content={article.metaDescription || article.excerpt} />
        {article.keywords && (
          <meta name="keywords" content={article.keywords.join(', ')} />
        )}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription || article.excerpt} />
        <meta property="og:image" content={article.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.metaDescription || article.excerpt} />
        <meta name="twitter:image" content={article.image} />
        <link rel="canonical" href={window.location.href} />
        
        {/* Add structured data for articles */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "image": article.image,
            "datePublished": article.publishedAt,
            "author": {
              "@type": "Organization",
              "name": "Armpit Care Guide"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Armpit Care Guide",
              "logo": {
                "@type": "ImageObject",
                "url": window.location.origin + "/favicon.ico"
              }
            },
            "description": article.metaDescription || article.excerpt
          })}
        </script>
      </Helmet>
      <Navbar />
      <main className="flex-grow">
        <ArticleHeader 
          title={article.title}
          image={article.image}
          publishedAt={article.publishedAt}
          readTime={article.readTime}
          category={article.category}
        />

        <div className="container-custom py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Main Content - Enhanced with more whitespace */}
            <div className="lg:col-span-8">
              {/* Featured Product Banner at top of content for high visibility */}
              {isHeyHumansArticle ? (
                <ProductCTA 
                  variant="banner" 
                  message="Try Hey Humans aluminum-free deodorant with plastic-free packaging!"
                  productName="Hey Humans Natural Deodorant"
                  productImage="/lovable-uploads/94e84ee7-e09e-4788-91d6-d985fc18c552.png"
                  linkType="heyhumans"
                />
              ) : (
                <ProductCTA variant="banner" message="Try the deodorant everyone's talking about!" />
              )}
              
              <ArticleContent content={article.content || ''} />
              
              <RecommendedProducts products={article.recommendedProducts || []} />
              
              <SourcesList sources={article.sources || []} />
              
              <ShareButtons />
            </div>

            {/* Sidebar - Updated with smoother spacing and card styling */}
            <ArticleSidebar 
              relatedArticles={article.relatedArticles} 
              isHeyHumansArticle={isHeyHumansArticle}
            />
          </div>
        </div>
        
        {/* Floating CTA */}
        <ProductCTA variant="floating" message="Ready to try something that works?" />
      </main>
      <Footer />
    </div>
  );
};

export default Article;
